import React, { useState } from 'react';
import { Box, ButtonV2, DatePickerInput, FormControl, InputV2, TextV2 } from '@withjoy/joykit';
import { ChevronDown, ChevronUp } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useSearchFormController, isoToDate } from './SearchForm.controller';

const ADVANCED_FIELDS = ['personId', 'guestKey', 'messageBatchId', 'stationeryOrderId'] as const;

export const SearchForm: React.FC = () => {
  const { tq } = useTranslation('messagesV2ServiceCenter');
  const {
    formik,
    gateOk,
    emailError,
    fromEmailError,
    replyToError,
    isCollapsed,
    setIsCollapsed,
    summaryChips,
    handlePhoneChange,
    handlePageSizeChange,
    handleStartDateChange,
    handleEndDateChange,
    handleStartDateClear,
    handleEndDateClear,
    today,
  } = useSearchFormController();

  const inputStyle = { fontSize: '13px' };

  const hasAdvancedValue = ADVANCED_FIELDS.some(f => !!formik.values[f]);
  const [showAdvancedManual, setShowAdvancedManual] = useState(false);
  // Always expand when any advanced field has a value so filled fields are never hidden
  const showAdvanced = hasAdvancedValue || showAdvancedManual;

  if (isCollapsed) {
    return (
      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        gap="8px"
        padding="10px 16px"
        border="1px solid #E8E8E8"
        borderRadius={8}
        background="#FFFFFF"
      >
        {summaryChips.map(chip => (
          <Box
            key={chip}
            paddingX="10px"
            paddingY="3px"
            borderRadius="100px"
            background="#F0F0F0"
            fontSize="12px"
            fontFamily="monospace"
            color="mono14"
          >
            {chip}
          </Box>
        ))}
        <ButtonV2
          size="sm"
          variant="ghost"
          marginLeft="auto"
          onClick={() => setIsCollapsed(false)}
        >
          <ChevronDown
            size="sm"
            marginRight="4px"
          />
          {tq`Edit search`}
        </ButtonV2>
      </Box>
    );
  }

  return (
    <Box
      as="form"
      onSubmit={formik.handleSubmit}
      border="1px solid #E8E8E8"
      borderRadius={8}
      padding="20px"
      background="#FFFFFF"
    >
      {/* Identifier fields — 3-col grid */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        gap="16px"
        marginBottom="16px"
      >
        <FormControl
          label={tq`To Email`}
          error={emailError}
        >
          <InputV2
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="user@example.com"
            style={inputStyle}
          />
        </FormControl>

        <FormControl
          label={tq`From email`}
          error={fromEmailError}
        >
          <InputV2
            name="fromEmail"
            value={formik.values.fromEmail}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="user@example.com"
            style={inputStyle}
          />
        </FormControl>

        <FormControl
          label={tq`Reply-to`}
          error={replyToError}
        >
          <InputV2
            name="replyTo"
            value={formik.values.replyTo}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="user@example.com"
            style={inputStyle}
          />
        </FormControl>

        <FormControl label={tq`To Phone`}>
          <Box>
            <InputV2
              name="phoneDisplay"
              value={formik.values.phoneDisplay}
              onChange={e => handlePhoneChange(e.target.value)}
              placeholder="+14155550100 or (415) 555-0100"
              style={inputStyle}
            />
            {formik.values.phoneE164 && (
              <TextV2
                typographyVariant="label3"
                color="mono10"
                marginTop="4px"
              >
                → {formik.values.phoneE164}
              </TextV2>
            )}
          </Box>
        </FormControl>

        <FormControl label={tq`Event ID`}>
          <InputV2
            name="eventId"
            value={formik.values.eventId}
            onChange={formik.handleChange}
            style={inputStyle}
          />
        </FormControl>
      </Box>

      {/* More identifiers disclosure */}
      <ButtonV2
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setShowAdvancedManual(s => !s)}
        marginBottom="16px"
      >
        {showAdvanced ? (
          <ChevronUp
            size="sm"
            marginRight="4px"
          />
        ) : (
          <ChevronDown
            size="sm"
            marginRight="4px"
          />
        )}
        {tq`More identifiers`}
      </ButtonV2>

      {showAdvanced && (
        <Box
          display="grid"
          gridTemplateColumns="repeat(3, 1fr)"
          gap="16px"
          marginBottom="16px"
        >
          <FormControl label={tq`Person ID`}>
            <InputV2
              name="personId"
              value={formik.values.personId}
              onChange={formik.handleChange}
              style={inputStyle}
            />
          </FormControl>

          <FormControl label={tq`Guest key`}>
            <InputV2
              name="guestKey"
              value={formik.values.guestKey}
              onChange={formik.handleChange}
              style={inputStyle}
            />
          </FormControl>

          <FormControl label={tq`Batch ID`}>
            <InputV2
              name="messageBatchId"
              value={formik.values.messageBatchId}
              onChange={formik.handleChange}
              style={inputStyle}
            />
          </FormControl>

          <FormControl label={tq`Card order ID`}>
            <InputV2
              name="stationeryOrderId"
              value={formik.values.stationeryOrderId}
              onChange={formik.handleChange}
              style={inputStyle}
            />
          </FormControl>
        </Box>
      )}

      {/* Optional filters */}
      <Box
        display="flex"
        flexWrap="wrap"
        gap="16px"
        marginBottom="16px"
      >
        <FormControl label={tq`Message type`}>
          <InputV2
            name="messageType"
            value={formik.values.messageType}
            onChange={formik.handleChange}
            placeholder="e.g. announcement"
            style={{ ...inputStyle, width: '160px' }}
          />
        </FormControl>

        <FormControl label={tq`Channel`}>
          <select
            name="channel"
            value={formik.values.channel}
            onChange={formik.handleChange}
            style={{ fontSize: '13px', padding: '8px 10px', borderRadius: '6px', border: '1px solid #D8D8D8', width: '120px' }}
          >
            <option value="all">{tq`All`}</option>
            <option value="email">{tq`Email`}</option>
            <option value="sms">{tq`SMS`}</option>
          </select>
        </FormControl>

        <FormControl label={tq`Created from`}>
          <DatePickerInput
            value={isoToDate(formik.values.startCreateDate)}
            onChange={handleStartDateChange}
            onInputChange={handleStartDateClear}
            maxDate={isoToDate(formik.values.endCreateDate) || today}
            dateFormat="MM/dd/yyyy"
            placeholder="MM/DD/YYYY"
          />
        </FormControl>

        <FormControl label={tq`Created to`}>
          <DatePickerInput
            value={isoToDate(formik.values.endCreateDate)}
            onChange={handleEndDateChange}
            onInputChange={handleEndDateClear}
            minDate={isoToDate(formik.values.startCreateDate) || undefined}
            maxDate={today}
            dateFormat="MM/dd/yyyy"
            placeholder="MM/DD/YYYY"
          />
        </FormControl>

        <FormControl label={tq`Page size`}>
          <select
            name="pageSize"
            value={formik.values.pageSize}
            onChange={e => handlePageSizeChange(Number(e.target.value))}
            style={{ fontSize: '13px', padding: '8px 10px', borderRadius: '6px', border: '1px solid #D8D8D8', width: '80px' }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </FormControl>
      </Box>

      {/* Gate warning */}
      {!gateOk && (
        <Box
          padding="10px 14px"
          borderRadius={6}
          background="rgb(255,243,229)"
          marginBottom="12px"
        >
          <TextV2
            typographyVariant="label3"
            color="mono14"
          >
            {tq`Add an email, phone, or event ID to enable search`}
          </TextV2>
        </Box>
      )}

      {/* Actions */}
      <Box
        display="flex"
        gap="12px"
        alignItems="center"
      >
        <ButtonV2
          type="submit"
          disabled={!gateOk}
        >
          {tq`Search`}
        </ButtonV2>
        <ButtonV2
          type="button"
          variant="ghost"
          size="sm"
          marginLeft="auto"
          onClick={() => setIsCollapsed(true)}
        >
          <ChevronUp
            size="sm"
            marginRight="4px"
          />
          {tq`Collapse`}
        </ButtonV2>
      </Box>
    </Box>
  );
};
